// Phase 2e.12 — Read-only renderer for a block's inline media plus
// shared tiles + lightbox hook reused by the legacy lesson-level
// attachment/photo rendering in the admin preview and the cover view.
//
// Why one file: the per-block media (step.attachment, step.photo,
// step.link, step.videoUrl) and the legacy lesson-level arrays
// (lesson.attachments[], lesson.photos[], lesson.resourcePaths[])
// share the same tile shapes — file pill, image thumbnail, link chip.
// Centralising them here keeps the in-app View affordance consistent
// across both surfaces (and across admin / teacher / cover viewers).
//
// Three modes change the available actions:
//   • admin   — View opens the in-app lightbox; Download button shown.
//   • teacher — Same as admin (their own lesson, their own files).
//   • cover   — View ONLY. Lightbox renders without a Download
//               button so cover teachers never end up with the
//               school's documents on a personal device.
//
// New: attachments and photos may carry a `blobPath` instead of a
// `dataUrl`. The lightbox + photo tile resolve the blobPath to a
// short-lived SAS URL on demand (useBlobReadUrl). Legacy lessons
// with inline dataUrls still render unchanged.

import { useCallback, useState } from 'react';
import { ExternalLink, Eye, Download, Paperclip, Link2, PlayCircle, Loader2, ChevronDown, FastForward } from 'lucide-react';
import type { LessonStep } from '@/lib/app-store';
import { stepAttachments, stepPhotos, stepLinks } from '@/lib/app-store';
import { MediaLightbox, type MediaLightboxItem } from './MediaLightbox';
import { useBlobReadUrl } from '@/lib/use-blob-read-url';
import { RichTextDisplay } from '@/components/ui/RichTextDisplay';

/** Read-only "If they finish early" callout — the per-block early-finishers
 *  note, shown identically to teacher / admin / cover. Renders nothing when
 *  the block carries no note. */
export function EarlyFinishersReadout({ step }: { step: LessonStep }) {
  const note = step.earlyFinishers?.trim();
  if (!note) return null;
  return (
    <div className="mt-2 rounded-lg border border-primary/30 bg-primary/5 p-2.5 flex items-start gap-2">
      <FastForward className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-primary mb-0.5">If they finish early</div>
        <div className="text-sm text-foreground leading-snug whitespace-pre-line">{note}</div>
      </div>
    </div>
  );
}

// ── Public API ─────────────────────────────────────────────────────

export type MediaMode = 'admin' | 'teacher' | 'cover';

interface BlockMediaPreviewProps {
  step: LessonStep;
  mode: MediaMode;
}

/** Renders the inline media on a single LessonStep (block-level). */
export function BlockMediaPreview({ step, mode }: BlockMediaPreviewProps) {
  const { open, lightbox } = useMediaLightbox(mode);

  // Merge legacy single fields + new arrays so blocks saved before
  // multi-media, and blocks authored after, both render every file.
  const photos = stepPhotos(step).filter(p => p.dataUrl || p.blobPath);
  const attachments = stepAttachments(step).filter(a => a.dataUrl || a.blobPath || a.name);
  const links = stepLinks(step).filter(l => l.url);
  const hasVideoUrl = Boolean(step.videoUrl);

  if (!photos.length && !attachments.length && !links.length && !hasVideoUrl) return null;
  const allowDownload = mode !== 'cover';

  return (
    <>
      <div className="mt-2 space-y-2">
        {photos.map((photo, i) => (
          <PhotoTile
            key={`p-${i}`}
            name={photo.name}
            dataUrl={photo.dataUrl}
            blobPath={photo.blobPath}
            allowDownload={allowDownload}
            onPreview={() => open({ name: photo.name, dataUrl: photo.dataUrl, blobPath: photo.blobPath })}
          />
        ))}

        {attachments.map((att, i) => {
          const hasSource = Boolean(att.dataUrl || att.blobPath);
          return (
            <FileTile
              key={`a-${i}`}
              name={att.name}
              type={att.type}
              size={att.size}
              dataUrl={att.dataUrl}
              blobPath={att.blobPath}
              allowDownload={allowDownload}
              onPreview={
                hasSource
                  ? () => open({ name: att.name, dataUrl: att.dataUrl, blobPath: att.blobPath, type: att.type })
                  : undefined
              }
            />
          );
        })}

        {links.map((link, i) => (
          <LinkTile key={`l-${i}`} url={link.url} label={link.label} kind="link" />
        ))}

        {hasVideoUrl && step.videoUrl && (
          <LinkTile url={step.videoUrl} kind="video" />
        )}
      </div>
      {lightbox}
    </>
  );
}

/** Read-only reveal of a block's answer key — for the cover teacher's
 *  eyes only, collapsed by default so it doesn't compete with the task
 *  itself but is one tap away. Renders nothing if the block has no
 *  answer key content. */
export function AnswerKeyReveal({ step, mode }: { step: LessonStep; mode: MediaMode }) {
  const [open, setOpen] = useState(false);
  const answerKey = step.answerKey;
  const hasDetail = !!answerKey?.detail && answerKey.detail.replace(/<[^>]+>/g, '').trim().length > 0;
  const hasMedia =
    stepAttachments(answerKey).length > 0 ||
    stepPhotos(answerKey).length > 0 ||
    stepLinks(answerKey).some(l => l.url);
  if (!answerKey || (!hasDetail && !hasMedia)) return null;

  // Reuse BlockMediaPreview's own attachment/photo/link rendering by
  // handing it a minimal synthetic step carrying just the answer key's
  // media (single legacy fields + arrays) in the fields it already knows
  // how to read — avoids a second copy of the tile/lightbox logic here.
  const mediaStep: LessonStep = {
    id: `${step.id}-answer-key`,
    minutes: 0,
    title: '',
    detail: '',
    attachment: answerKey.attachment,
    photo: answerKey.photo,
    link: answerKey.link,
    attachments: answerKey.attachments,
    photos: answerKey.photos,
    links: answerKey.links,
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-1.5 -ml-2 px-2 py-2.5 sm:py-1 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
      >
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? '' : '-rotate-90'}`} />
        {open ? 'Hide answer key' : 'Show answer key'}
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-dashed border-primary/30 bg-primary/5 p-3">
          {hasDetail && (
            <RichTextDisplay content={answerKey.detail} className="text-sm text-foreground/90" maxHeight="10rem" />
          )}
          <BlockMediaPreview step={mediaStep} mode={mode} />
        </div>
      )}
    </div>
  );
}

/** Hook: a lightbox controller plus the modal node to render. The
 *  caller calls `open(item)` to display, and embeds `lightbox` in
 *  its JSX wherever convenient (the modal portals to body anyway). */
export function useMediaLightbox(mode: MediaMode) {
  const [item, setItem] = useState<MediaLightboxItem | null>(null);
  const open = useCallback((it: MediaLightboxItem) => setItem(it), []);
  const close = useCallback(() => setItem(null), []);
  const allowDownload = mode !== 'cover';
  const lightbox = (
    <MediaLightbox item={item} allowDownload={allowDownload} onClose={close} />
  );
  return { open, close, lightbox };
}

// ── Shared tiles ───────────────────────────────────────────────────

export function PhotoTile({
  name,
  dataUrl,
  blobPath,
  allowDownload,
  onPreview,
}: {
  name: string;
  /** Either dataUrl (legacy) or blobPath (new) must be present. */
  dataUrl?: string;
  blobPath?: string;
  allowDownload: boolean;
  /** Omit when the attachment has no source at all. Legacy rows written by
   *  FileUploadSection above its 1 MB inline limit carry neither dataUrl nor
   *  blobPath, and the lightbox renders an empty black modal for those: every
   *  body branch needs a resolved URL. FileTile already guards this way. */
  onPreview?: () => void;
}) {
  // Resolve blob path to a SAS URL for the thumbnail. dataUrl wins
  // when present (covers legacy lessons and just-uploaded photos).
  const { url: sasUrl, loading } = useBlobReadUrl(dataUrl ? undefined : blobPath, name);
  const src = dataUrl ?? sasUrl;

  return (
    <div className="relative inline-block group">
      <button
        type="button"
        onClick={onPreview}
        disabled={!onPreview}
        title={onPreview ? `Open ${name}` : name}
        className={`block rounded-lg overflow-hidden border border-border/60 transition-colors ${
          onPreview ? 'hover:border-primary/50 cursor-zoom-in' : 'cursor-default'
        }`}
      >
        {src ? (
          <img
            src={src}
            alt={name}
            className="block max-h-48 max-w-full object-contain bg-muted/30"
          />
        ) : (
          <div className="w-44 h-32 flex items-center justify-center bg-muted/30 text-muted-foreground">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="text-xs">No preview</span>}
          </div>
        )}
      </button>
      <div className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 pointer-events-none">
        {onPreview && (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onPreview(); }}
            className="pointer-events-auto p-2 bg-white/95 rounded-lg text-foreground hover:bg-white transition-colors shadow"
            title="Open"
          >
            <Eye size={14} />
          </button>
        )}
        {allowDownload && src && (
          <a
            href={src}
            download={name}
            target={blobPath ? '_blank' : undefined}
            rel={blobPath ? 'noopener noreferrer' : undefined}
            className="pointer-events-auto p-2 bg-white/95 rounded-lg text-foreground hover:bg-white transition-colors shadow"
            title="Download"
            onClick={e => e.stopPropagation()}
          >
            <Download size={14} />
          </a>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground mt-1 truncate max-w-[200px]">{name}</p>
    </div>
  );
}

export function FileTile({
  name,
  type,
  size,
  dataUrl,
  blobPath,
  allowDownload,
  onPreview,
}: {
  name: string;
  type?: string;
  size?: number;
  dataUrl?: string;
  blobPath?: string;
  allowDownload: boolean;
  /** Omit when the attachment has no source (legacy "teacher's copy"
   *  rows where the data URL was stripped); the View button hides. */
  onPreview?: () => void;
}) {
  // Resolve blob path only when needed for the Download anchor.
  // The View button uses the lightbox which resolves its own URL,
  // so we don't always need this — but if the caller wants to
  // download, we need a usable href. dataUrl wins when present.
  const { url: sasUrl } = useBlobReadUrl(dataUrl ? undefined : blobPath, name);
  const downloadHref = dataUrl ?? sasUrl;

  const ext = name.split('.').pop()?.toUpperCase() ?? '';
  const sizeKb = size ? Math.round(size / 1024) : null;
  const hasSource = Boolean(dataUrl || blobPath);

  return (
    <div className="flex items-center gap-2 p-2.5 bg-card rounded-lg border border-border/60 text-sm">
      <Paperclip size={13} className="text-primary flex-shrink-0" />
      <span className="text-foreground font-medium truncate flex-1">{name}</span>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {ext && <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded font-mono">{ext}</span>}
        {sizeKb !== null && <span className="text-[10px] text-muted-foreground">{sizeKb} KB</span>}
        {hasSource && onPreview ? (
          <>
            <button
              type="button"
              onClick={onPreview}
              className="p-1 rounded-md bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
              title="View in-app"
            >
              <Eye size={13} />
            </button>
            {allowDownload && downloadHref && (
              <a
                href={downloadHref}
                download={name}
                target={blobPath ? '_blank' : undefined}
                rel={blobPath ? 'noopener noreferrer' : undefined}
                className="p-1 rounded-md bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                title="Download"
                onClick={e => e.stopPropagation()}
              >
                <Download size={13} />
              </a>
            )}
          </>
        ) : (
          <span className="text-[10px] text-muted-foreground italic">Teacher's copy</span>
        )}
      </div>
    </div>
  );
}

function LinkTile({
  url,
  label,
  kind,
}: {
  url: string;
  label?: string;
  kind: 'link' | 'video';
}) {
  const Icon = kind === 'video' ? PlayCircle : Link2;
  const displayLabel = label?.trim() || url;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 p-2 bg-card rounded-lg border border-border/60 hover:border-primary/40 text-sm text-primary transition-colors break-all"
      onClick={e => e.stopPropagation()}
    >
      <Icon size={13} className="flex-shrink-0" />
      <span className="flex-1 truncate">{displayLabel}</span>
      <ExternalLink size={11} className="flex-shrink-0 opacity-60" />
    </a>
  );
}
